import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/new-components/ui/button';
import { SecuritiesDisclaimer } from '../components/Auth/SecuritiesDisclaimer';
import { useUser } from '../contexts/UserContext';
import { BRAND_ASSETS, TENANT } from '../config';

const API_BASE_URL = TENANT.defaults.apiBaseUrl;

/**
 * Landing page for the email verification link (carries a ?token=). Shares the auth
 * design language with VerifyEmailRequired: centered logo, portal typography, token
 * colors, our Button, and the securities disclaimer footer.
 */
const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email…');
  const { isAuthenticated, refreshUser } = useUser();
  // Read the latest auth state at redirect time (it may resolve after mount), not the mount value.
  const authedRef = useRef(isAuthenticated);
  authedRef.current = isAuthenticated;

  const token = searchParams.get('token');

  useEffect(() => {
    // Guard against setState/navigate after unmount: the request is in flight and the
    // success path schedules a 3s redirect — both can outlive this component.
    let cancelled = false;
    let redirectTimer: ReturnType<typeof setTimeout>;

    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('This verification link is missing its token. Request a new link and try again.');
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/auth/verify-email`, { params: { token } });
        if (cancelled) return;

        if (response.data.success) {
          setStatus('success');
          setMessage("You're all set.");
          redirectTimer = setTimeout(async () => {
            if (cancelled) return;
            if (authedRef.current) {
              // Same browser as sign-up: refresh so email_verified flips true (the portal gate
              // reads it), then go straight in — no need to sign in again.
              try {
                await refreshUser();
              } catch {
                /* fall through — the portal gate re-checks verification */
              }
              if (!cancelled) navigate('/portal/invest/properties');
            } else {
              navigate('/login', {
                state: { message: 'Email verified. Please sign in to continue.' },
              });
            }
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.message || "We couldn't verify your email. Please try again.");
        }
      } catch (error: any) {
        if (cancelled) return;
        console.error('Email verification error:', error);
        setStatus('error');
        if (error.response?.data?.message) {
          setMessage(error.response.data.message);
        } else if (error.response?.status === 400) {
          setMessage('This verification link is invalid or has expired.');
        } else {
          setMessage('Something went wrong during verification. Please try again.');
        }
      }
    };

    verifyEmail();

    return () => {
      cancelled = true;
      clearTimeout(redirectTimer);
    };
  }, [token, navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex flex-col portal-typography">
      {/* Header — centered logo */}
      <div className="px-4 md:px-6 pt-4 md:pt-6 pb-3 flex justify-center">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <img
            src={BRAND_ASSETS.logo.primary}
            alt={TENANT.branding.name}
            className="h-11 object-contain"
          />
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="tab-panel-in max-w-xl w-full text-center">
          {status === 'loading' && (
            <div className="space-y-3">
              <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground animate-spin" aria-hidden="true" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                Verifying your email
              </h1>
            </div>
          )}

          {status === 'success' && (
            <>
              <div className="space-y-3">
                <CheckCircle2 className="w-12 h-12 mx-auto text-[hsl(var(--success))]" aria-hidden="true" />
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                  Email verified
                </h1>
                <p className="text-lg text-foreground [text-wrap:balance]">{message}</p>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground">
                <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
                Redirecting…
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="space-y-3">
                <XCircle className="w-12 h-12 mx-auto text-destructive" aria-hidden="true" />
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium [text-wrap:balance]">
                  Verification failed
                </h1>
                <p className="text-base text-muted-foreground [text-wrap:balance]">{message}</p>
              </div>
              <div className="mt-8 max-w-md mx-auto space-y-3">
                <Button onClick={() => navigate('/login')} className="w-full h-14 text-lg">
                  Go to sign in
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/register')}
                  className="w-full h-14 text-lg"
                >
                  Create a new account
                </Button>
                <p className="pt-2 text-sm text-muted-foreground">
                  Need help?{' '}
                  <a
                    href={`mailto:${TENANT.branding.supportEmail}`}
                    className="font-semibold text-foreground underline"
                  >
                    Contact support
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <SecuritiesDisclaimer className="pb-6 px-4" />
    </div>
  );
};

export default EmailVerification;
